import { useUploadAttachmentMutation } from '../../../../../apollo/sites'

export interface AttachmentMetadata {
  attachmentName: string
  originalName: string
  size: number
  contentType: string
}

const PDF_MIME_TYPE = 'application/pdf'

/**
 * Uploads a file attachment to S3 using presigned POST
 * @param file The file to upload
 * @param uploadAttachmentMutation The generated mutation function
 * @returns Upload metadata
 * @throws Error if upload fails
 */
export const uploadAttachment = async (
  file: File,
  uploadAttachmentMutation: ReturnType<typeof useUploadAttachmentMutation>[0]
): Promise<AttachmentMetadata> => {
  const validationResult = await validateAttachment(file)
  if (!validationResult.isValid) {
    throw new Error(validationResult.error || 'Invalid file')
  }

  const { data, errors } = await uploadAttachmentMutation({
    variables: {
      contentType: file.type || PDF_MIME_TYPE
    }
  })

  if (errors) {
    throw new Error(
      `Failed to get upload presigned URL: ${errors
        .map((e) => e.message)
        .join(', ')}`
    )
  } else if (!data?.uploadAttachment) {
    throw new Error('No upload attachment data returned')
  }

  const { attachmentName, postUrl, formFields } = data.uploadAttachment

  const formData = new FormData()

  formFields.forEach((field: { name: string; value: string }) => {
    formData.append(field.name, field.value)
  })

  formData.append('file', file)

  const uploadResponse = await fetch(postUrl, {
    method: 'POST',
    body: formData
  })

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text()
    console.error('Upload failed:', uploadResponse.status, errorText)
    throw new Error(`Upload failed: ${uploadResponse.statusText}`)
  }

  return {
    attachmentName: attachmentName,
    originalName: file.name,
    size: file.size,
    contentType: file.type || PDF_MIME_TYPE
  }
}

/**
 * Validates that a file is a PDF and within size limits
 * @param file The file to validate
 * @param maxSize Maximum file size in bytes (default: 10MB)
 * @returns Validation result with error message if invalid
 */
export const validateAttachment = (
  file: File,
  maxSize: number = 10 * 1024 * 1024
): { isValid: boolean; error?: string } => {
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    return {
      isValid: false,
      error: 'Only PDF files are allowed'
    }
  }

  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `File size must be less than ${maxSize / 1024 / 1024}MB`
    }
  }

  return { isValid: true }
}

/**
 * Custom hook for managing attachment uploads
 */
export const useAttachmentUpload = () => {
  const [uploadAttachmentMutation, { loading, error }] =
    useUploadAttachmentMutation()

  const upload = async (file: File): Promise<AttachmentMetadata> => {
    return uploadAttachment(file, uploadAttachmentMutation)
  }

  return {
    upload,
    loading,
    error
  }
}
